import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismTable } from '@mylighthouse/prism-ember';
import { and } from 'ember-truth-helpers';
import HotelRatesTablesCellsThHotel from 'lumen/components/hotel/rates/tables/cells/th-hotel';
import ParityTablesCellsTdDate from 'lumen/components/parity/tables/cells/td-date';
import ParityTablesCellsTdPosBaserate from 'lumen/components/parity/tables/cells/td-pos-baserate';
import ParityTablesCellsTdPosCountry from 'lumen/components/parity/tables/cells/td-pos-country';
import ParityTablesCellsTdPosLinkout from 'lumen/components/parity/tables/cells/td-pos-linkout';
import ParityTablesCellsTdPosLowestRateOverOtas from 'lumen/components/parity/tables/cells/td-pos-lowest-rate-over-otas';
import ParityTablesCellsTdPosOtasWithIssues from 'lumen/components/parity/tables/cells/td-pos-otas-with-issues';

const COLUMNS = {
  DATE: 'date',
  POS: 'pos',
  RATE: 'rate',
  LOWEST_RATE_MAJOR_OTAS: 'lowest-rate-major-otas',
  MAJOR_OTAS: 'major-otas',
  LOWEST_RATE_OTHER_OTAS: 'lowest-rate-other-otas',
  OTHER_OTAS: 'other-otas',
  LIVE_VIEW: 'live-view',
};

interface ParityTablesTableParityPosSignature {
  Args: {
    isLoading: unknown;
    message: unknown;
    model: unknown;
  };
}

export default class ParityTablesTableParityPos extends Component<ParityTablesTableParityPosSignature> {
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get columns() {
    const columns = [
      {
        name: this.intl.t('general.date'),
        valuePath: COLUMNS.DATE,
        thComponent: HotelRatesTablesCellsThHotel,
        tdComponent: ParityTablesCellsTdDate,
        textAlign: 'left',
        minWidth: 120,
        maxWidth: 120,
        hasDayLiveShop: true,
        parityType: 'parity_pos',
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        firstPos: this.args.model[0]?.pos,
      },
      {
        name: this.intl.t('parity.pos.pos'),
        valuePath: COLUMNS.POS,
        thComponent: HotelRatesTablesCellsThHotel,
        tdComponent: ParityTablesCellsTdPosCountry,
        textAlign: 'center',
        minWidth: 50,
        maxWidth: 50,
        hasMargin: false,
      },
      {
        name: this.intl.t('parity.pos.hotel_rate'),
        valuePath: COLUMNS.RATE,
        thComponent: HotelRatesTablesCellsThHotel,
        tdComponent: ParityTablesCellsTdPosBaserate,
        textAlign: 'right',
        minWidth: 120,
        maxWidth: 120,
        hasRightBorder: true,
        infoText: this.intl.t('parity.pos.hotel_rate_info'),
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        availableOTAs: this.args.availableOTAs,
      },
      {
        name: this.intl.t('general.major_otas'),
        valuePath: COLUMNS.MAJOR_OTAS,
        thComponent: HotelRatesTablesCellsThHotel,
        tdComponent: ParityTablesCellsTdPosOtasWithIssues,
        minWidth: 220,
        maxWidth: 300,
        textAlign: 'left',
        searchMajorOtas: true,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        availableOTAs: this.args.availableOTAs,
      },
      {
        name: this.intl.t('rates.lowest_rate'),
        valuePath: COLUMNS.LOWEST_RATE_MAJOR_OTAS,
        thComponent: HotelRatesTablesCellsThHotel,
        tdComponent: ParityTablesCellsTdPosLowestRateOverOtas,
        textAlign: 'right',
        minWidth: 120,
        maxWidth: 120,
        hasMargin: false,
        hasRightBorder: true,
        searchMajorOtas: true,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        availableOTAs: this.args.availableOTAs,
      },
      {
        name: this.intl.t('general.other_otas'),
        valuePath: COLUMNS.OTHER_OTAS,
        thComponent: HotelRatesTablesCellsThHotel,
        tdComponent: ParityTablesCellsTdPosOtasWithIssues,
        textAlign: 'left',
        minWidth: 220,
        maxWidth: 300,
        searchMajorOtas: false,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        availableOTAs: this.args.availableOTAs,
      },
      {
        name: this.intl.t('rates.lowest_rate'),
        thComponent: HotelRatesTablesCellsThHotel,
        tdComponent: ParityTablesCellsTdPosLowestRateOverOtas,
        valuePath: COLUMNS.LOWEST_RATE_OTHER_OTAS,
        textAlign: 'right',
        minWidth: 120,
        maxWidth: 120,
        hasMargin: false,
        hasRightBorder: true,
        searchMajorOtas: false,
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        availableOTAs: this.args.availableOTAs,
      },
      {
        name: this.intl.t('parity.pos.live'),
        valuePath: COLUMNS.LIVE_VIEW,
        thComponent: HotelRatesTablesCellsThHotel,
        tdComponent: ParityTablesCellsTdPosLinkout,
        textAlign: 'right',
        minWidth: 120,
        maxWidth: 120,
        hasRightBorder: true,
        infoTextTitle: this.intl.t('parity.live_view').toString(),
        infoText: this.intl.t('parity.live_view_tooltip').toString(),
      },
    ];

    return columns;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rows() {
    // @ts-expect-error: Incorrect type
    const rows = [];
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.args.model.forEach((period) => {
      const row = {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        [COLUMNS.DATE]: period,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
        [COLUMNS.POS]: period.pos,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        [COLUMNS.RATE]: period,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        [COLUMNS.LOWEST_RATE_MAJOR_OTAS]: period,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        [COLUMNS.MAJOR_OTAS]: period,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        [COLUMNS.LOWEST_RATE_OTHER_OTAS]: period,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        [COLUMNS.OTHER_OTAS]: period,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        [COLUMNS.LIVE_VIEW]: period,
      };
      rows.push(row);
    });

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return rows;
  }

  <template>
    <PrismTable
      {{! @glint-expect-error: Incorrect type }}
      @columns={{this.columns}}
      {{! @glint-expect-error: Incorrect type }}
      @isLoading={{@isLoading}}
      {{! @glint-expect-error: Incorrect type }}
      @overlayMessage={{and @message.showOverlay @message.message}}
      @rows={{this.rows}}
      @widthConstraint="gte-container-slack"
    />
  </template>
}
